import React, { useCallback, useEffect, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ListContainer } from "../createGigModal.styled";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { CompanyCustomer } from "model/Company";
import { isSettingEnabled } from "model/settings.logic";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Api } from "services/api/api.service";
import { getWorkerImg } from "services/firebase/firebase.service";
import type { Worker as ModalWorker } from "../../../organizationalStructure/chooseWorkersModal.types";
import {
  CopyButtonWrap,
  CopyButtonSlot,
  CustomerDropdownWrap,
  TeamDropdownWrap,
  TrashWrap,
  WorkerLeft,
  WorkerRight,
  WorkerRow,
} from "./workerList.styled";

export type Worker = ModalWorker;

type Props = {
  setSelectedWorkers: React.Dispatch<React.SetStateAction<Worker[]>>;
  selectedWorkers: Worker[];
  onRepeatScheduleClick: (worker: Worker) => void;
};

const normalizeKey = (w: Worker) => String(w.workerId ?? w.id);

const teamValue = (team?: { id?: number; workGroupId?: number }) => {
  if (!team) return undefined;
  const value = team.id ?? team.workGroupId;
  return value != null ? Number(value) : undefined;
};

const teamLabel = (team?: {
  companyUnitName?: string | null;
  name?: string | null;
  workGroupName?: string | null;
}) => {
  const unitName = String(team?.companyUnitName ?? "").trim();
  const teamName = String(team?.name ?? team?.workGroupName ?? "").trim();

  if (!unitName) return teamName;
  if (!teamName) return unitName;
  return `${unitName} - ${teamName}`;
};

const normalizeSelectedTeamId = (
  selectedTeamId?: number,
): number | undefined => {
  if (selectedTeamId == null) return undefined;
  const parsed = Number(selectedTeamId);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
};

export const WorkerList: React.FC<Props> = memo((props) => {
  const { t } = useTranslation();
  const { onRepeatScheduleClick, selectedWorkers, setSelectedWorkers } = props;
  const [companyState, ,] = useCompanyStore();
  const [workerAvatarByExternalId, setWorkerAvatarByExternalId] = useState<
    Record<string, string>
  >({});
  const [customers, setCustomers] = useState<CompanyCustomer[]>([]);
  const customersEnabled = isSettingEnabled(
    companyState.companySettings,
    "CustomersEnabled",
  );

  const customerOptions: SearchableDropdownOption[] = customers
    .filter((customer) => customer.id != null)
    .map((customer) => ({
      value: String(customer.id),
      label: customer.name,
    }));

  useEffect(() => {
    const companyId = companyState.company?.id;
    if (companyId) {
      Api()
        .company.getCompanyCustomers(companyId)
        .then((result) => {
          setCustomers(result.data);
        })
        .catch(() => undefined);
    }
  }, [companyState.company?.id]);

  useEffect(() => {
    if (!selectedWorkers?.length) return;

    let hasChanges = false;
    const nextSelectedWorkers = selectedWorkers.map((w) => {
      const groups = w.workGroups ?? [];
      const normalizedSelectedTeamId = normalizeSelectedTeamId(
        w.selectedTeamId,
      );
      if (groups.length === 1 && !normalizedSelectedTeamId) {
        hasChanges = true;
        return { ...w, selectedTeamId: teamValue(groups[0]) };
      }
      if (w.selectedTeamId !== normalizedSelectedTeamId) {
        hasChanges = true;
        return { ...w, selectedTeamId: normalizedSelectedTeamId };
      }
      return w;
    });

    if (hasChanges) {
      setSelectedWorkers(nextSelectedWorkers);
    }
  }, [selectedWorkers, setSelectedWorkers]);

  const handleRemove = useCallback(
    (workerKey: string) => {
      setSelectedWorkers((prev) =>
        prev.filter((w) => normalizeKey(w) !== workerKey),
      );
    },
    [setSelectedWorkers],
  );

  const handleTeamChange = useCallback(
    (workerKey: string, value: string) => {
      const parsedTeamId = Number(value);
      setSelectedWorkers((prev) =>
        prev.map((w) =>
          normalizeKey(w) === workerKey
            ? {
                ...w,
                selectedTeamId:
                  Number.isFinite(parsedTeamId) && parsedTeamId > 0
                    ? parsedTeamId
                    : undefined,
              }
            : w,
        ),
      );
    },
    [setSelectedWorkers],
  );

  const handleCustomerChange = useCallback(
    (workerKey: string, value: string) => {
      setSelectedWorkers((prev) =>
        prev.map((w) =>
          normalizeKey(w) === workerKey
            ? {
                ...w,
                selectedCustomerProduct: value || undefined,
              }
            : w,
        ),
      );
    },
    [setSelectedWorkers],
  );

  const resolveCustomerValue = useCallback(
    (selectedCustomerProduct?: string): string => {
      if (!selectedCustomerProduct) return "";

      const byId = customerOptions.find(
        (option) => option.value === selectedCustomerProduct,
      );
      if (byId) return byId.value;

      const byName = customerOptions.find(
        (option) => option.label === selectedCustomerProduct,
      );
      return byName?.value ?? "";
    },
    [customerOptions],
  );

  useEffect(() => {
    const externalIds = Array.from(
      new Set(
        selectedWorkers
          .map((worker) => worker.externalId)
          .filter((externalId): externalId is string => Boolean(externalId)),
      ),
    );
    if (externalIds.length === 0) return;

    let isMounted = true;
    Promise.all(
      externalIds.map(async (externalId) => {
        const imageUrl = await getWorkerImg(externalId);
        return { externalId, imageUrl: imageUrl ?? "" };
      }),
    ).then((rows) => {
      if (!isMounted) return;
      setWorkerAvatarByExternalId((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (row.imageUrl) {
            next[row.externalId] = row.imageUrl;
          }
        });
        return next;
      });
    });

    return () => {
      isMounted = false;
    };
  }, [selectedWorkers]);

  return (
    <>
      <ListContainer>
        {selectedWorkers.length > 0 &&
          selectedWorkers.map((worker) => {
            const workerKey = normalizeKey(worker);
            const groups = worker.workGroups ?? [];

            const options = groups
              .map((team) => ({
                value: teamValue(team),
                label: teamLabel(team) || t("CreateShift.UnnamedTeam"),
              }))
              .filter((o) => o.value != null);
            const teamOptions: SearchableDropdownOption[] = options.map(
              (option) => ({
                value: String(option.value ?? ""),
                label: option.label,
              }),
            );

            const normalizedSelectedTeamId = normalizeSelectedTeamId(
              worker.selectedTeamId,
            );
            const value =
              normalizedSelectedTeamId ??
              (groups.length === 1 ? teamValue(groups[0]) : undefined);

            const isLocked = groups.length === 1;
            const currentEmployment = worker.employmentInfo?.current;
            const subtitle = t(
              `Staff.employmentType${currentEmployment?.employmentType ?? ""}`,
              {
                defaultValue: currentEmployment?.employmentType ?? "-",
              },
            );

            return (
              <React.Fragment key={`worker-${workerKey}`}>
                <WorkerRow>
                  <WorkerLeft>
                    <AvatarChip
                      title={`${worker.firstName} ${worker.lastName}`}
                      subTitle={subtitle}
                      avatarUrl={
                        worker.externalId
                          ? workerAvatarByExternalId[worker.externalId]
                          : undefined
                      }
                    />
                  </WorkerLeft>

                  <WorkerRight>
                    {customersEnabled && (
                      <CustomerDropdownWrap>
                        <SearchableDropdown
                          options={customerOptions}
                          value={resolveCustomerValue(
                            worker.selectedCustomerProduct,
                          )}
                          placeholder={t("CompanyCustomers.Customer", {
                            defaultValue: "Customer",
                          })}
                          fullWidth
                          onChange={(val: string) =>
                            handleCustomerChange(workerKey, val)
                          }
                        />
                      </CustomerDropdownWrap>
                    )}
                    <TeamDropdownWrap>
                      <SearchableDropdown
                        options={teamOptions}
                        value={value != null ? String(value) : ""}
                        placeholder={t("CreateShift.ChooseTeam")}
                        disabled={isLocked}
                        fullWidth
                        onChange={(val: string) =>
                          handleTeamChange(workerKey, val)
                        }
                      />
                    </TeamDropdownWrap>
                    <CopyButtonSlot>
                      <TooltipHover
                        text={t("CreateShift.RepeatSchedule")}
                        arrow="down"
                      >
                        <CopyButtonWrap
                          type="button"
                          aria-label={t("CreateShift.RepeatSchedule")}
                          onClick={() => onRepeatScheduleClick(worker)}
                        >
                          <CustomIcon
                            name="v2/calendar"
                            size="22px"
                            color={ColorV2.Blue1}
                          />
                        </CopyButtonWrap>
                      </TooltipHover>
                    </CopyButtonSlot>
                    <TrashWrap>
                      <CustomIcon
                        name="v2/trash"
                        size="24px"
                        color={ColorV2.NegativeRed}
                        padding="0px"
                        hover
                        onClick={() => handleRemove(workerKey)}
                      />
                    </TrashWrap>
                  </WorkerRight>
                </WorkerRow>
              </React.Fragment>
            );
          })}
      </ListContainer>
    </>
  );
});
